// src/context/LocaleContext.jsx
import React, {
  createContext,
  useContext,
  useEffect,
  useLayoutEffect,
  useState,
  useCallback,
} from "react";

import api from "../api/axios";
import { useTranslation } from "react-i18next";
import { isNativeApp } from "../native/runtime";
import { getNativeRoutingSnapshot } from "../native/regionRouting";
import {
  UI_LANGUAGE_SOURCE_MANUAL,
  UI_LANGUAGE_SOURCE_STORAGE_KEY,
  UI_LANGUAGE_STORAGE_KEY,
  initializeNativeUiLanguagePreference,
  normalizeContourUiLanguage,
} from "../i18n/deviceLanguage";

const LocaleContext = createContext({
  country: null,
  language: "en",
  isRussianContour: false,
  setCountry: () => {},
  setLanguage: () => {},
});

function isRussianWebHost() {
  if (typeof window === "undefined") return false;
  return String(window.location?.hostname || "").trim().toLowerCase() === "ru.vaultgrid.net";
}

function isRussianNativeRegion(routing) {
  if (!isNativeApp() || !routing) return false;
  return routing.accountRegion === "RU" || routing.registrationRegion === "RU";
}

function normalizeSupportedLanguage(value, russianContour = false) {
  return normalizeContourUiLanguage(value, russianContour ? "RU" : "EU");
}

function normalizeCountryCode(value) {
  const raw = String(value || "").trim().toUpperCase();
  if (!raw) return null;
  if (raw === "RU" || raw === "SK") return raw;
  return raw;
}

export const LocaleProvider = ({ children }) => {
  const nativeApp = isNativeApp();
  const initialNativeRouting = nativeApp ? getNativeRoutingSnapshot() : null;
  const [nativeRouting, setNativeRouting] = useState(initialNativeRouting);
  const isRussianContour = isRussianWebHost() || isRussianNativeRegion(nativeRouting);
  const [country, setCountryState] = useState(() => (
    initialNativeRouting?.country || (initialNativeRouting?.registrationRegion === "RU" ? "RU" : null)
  ));
  const [language, setLanguage] = useState(() => (
    normalizeSupportedLanguage(
      nativeApp ? initializeNativeUiLanguagePreference() : initialNativeRouting?.language || "en",
      isRussianContour
    )
  ));

  // ✅ Берём правильный экземпляр i18n из хука
  const { i18n } = useTranslation();

  useLayoutEffect(() => {
    const contourLanguage = normalizeSupportedLanguage(language, isRussianContour);
    if (i18n.language !== contourLanguage) i18n.changeLanguage(contourLanguage);
    if (typeof document !== "undefined") {
      const marketingLanguage = typeof window !== "undefined"
        ? window.location.pathname.match(/^\/(?:for-organizers|organizers|for-participants|participants)\/(en|sk|cs)\/?$/)?.[1]
        : null;
      document.documentElement.lang = marketingLanguage || contourLanguage;
    }
  }, [i18n, isRussianContour, language]);

  const applyLanguage = useCallback(
    (newLanguage, { persist = true, source = "manual" } = {}) => {
      if (nativeApp && source === "profile") return;
      const lang = normalizeSupportedLanguage(newLanguage, isRussianContour);

      setLanguage(lang);

      if (typeof document !== "undefined") {
        const marketingLanguage = typeof window !== "undefined"
          ? window.location.pathname.match(/^\/(?:for-organizers|organizers|for-participants|participants)\/(en|sk|cs)\/?$/)?.[1]
          : null;
        document.documentElement.lang = marketingLanguage || lang;
      }

      if (persist) {
        try {
          localStorage.setItem(UI_LANGUAGE_STORAGE_KEY, lang);
          if (nativeApp) {
            localStorage.setItem(UI_LANGUAGE_SOURCE_STORAGE_KEY, source);
          }
        } catch (e) {
          console.warn("Failed to persist ui language", e);
        }
      }

      try {
        i18n.changeLanguage(lang);
      } catch (e) {
        console.error("Failed to change i18n language", e);
      }
    },
    [i18n, isRussianContour, nativeApp]
  );

  const applyCountry = useCallback(
    (newCountry, { persist } = { persist: true }) => {
      const normalizedCountry = normalizeCountryCode(newCountry);
      setCountryState(normalizedCountry);

      if (persist) {
        try {
          if (normalizedCountry) {
            localStorage.setItem("vg_profile_country", normalizedCountry);
          } else {
            localStorage.removeItem("vg_profile_country");
          }
        } catch (e) {
          console.warn("Failed to persist profile country", e);
        }
      }

    },
    []
  );

  useEffect(() => {
    // 0) Пробуем явный выбор языка интерфейса (гость или залогиненный).
    //    Для гостя он имеет приоритет над IP-дефолтом.
    let hasStoredUiLang = false;

    try {
      const storedUiLang = localStorage.getItem(UI_LANGUAGE_STORAGE_KEY);
      const storedUiLangSource = localStorage.getItem(UI_LANGUAGE_SOURCE_STORAGE_KEY);
      if (storedUiLang && (!nativeApp || storedUiLangSource === UI_LANGUAGE_SOURCE_MANUAL)) {
        hasStoredUiLang = true;
        applyLanguage(storedUiLang, { persist: true, source: "manual" });
      }
    } catch (e) {
      console.warn("Failed to read vg_ui_language from localStorage", e);
    }

    // 1) IP-гео через наш бэкенд.
    //    Правило:
    //    - RU contour -> ru
    //    - SK -> sk by default
    //    - everything else -> en by default
    //    Явный выбор пользователя в localStorage не трогаем.
    const detectByIP = async () => {
      if (nativeApp) {
        const routing = getNativeRoutingSnapshot();
        const nativeCountry = routing.country
          || (routing.registrationRegion === "RU" ? "RU" : null);
        applyCountry(nativeCountry, { persist: false });
        if (!hasStoredUiLang) {
          applyLanguage(initializeNativeUiLanguagePreference(), {
            persist: true,
            source: "device",
          });
        }
        return;
      }

      try {
        const res = await api.get("/accounts/ui_language/");
        const countryCode = normalizeCountryCode(res.data?.country);
        const detectedLanguage = normalizeSupportedLanguage(res.data?.language);

        // НЕ сохраняем IP-определённые значения в localStorage.
        applyCountry(countryCode, { persist: false });
        if (!hasStoredUiLang) {
          applyLanguage(detectedLanguage, { persist: false });
        }
      } catch (e) {
        console.warn("IP geo detection failed", e);
        // Фолбэк: остаёмся на en, но не ломаем явный выбор пользователя.
        applyCountry(null, { persist: false });
        if (!hasStoredUiLang) {
          applyLanguage("en", { persist: false });
        }
      }
    };

    detectByIP();
  }, [applyCountry, applyLanguage, nativeApp]);

  useEffect(() => {
    if (!nativeApp || typeof window === "undefined") return undefined;

    const handleRoutingChange = (event) => {
      const routing = event?.detail || getNativeRoutingSnapshot();
      setNativeRouting(routing);
      applyCountry(
        routing.country || (routing.registrationRegion === "RU" ? "RU" : null),
        { persist: false }
      );
    };
    window.addEventListener("vg:native-routing-changed", handleRoutingChange);
    return () => window.removeEventListener("vg:native-routing-changed", handleRoutingChange);
  }, [applyCountry, nativeApp]);

  const value = {
    country,
    language,
    isRussianContour,
    /**
     * Вызываешь этот метод из формы / профиля / Auth,
     * когда пользователь выбирает страну.
     * Например: setCountry("RU")
     * (это попадёт в localStorage и станет приоритетом над IP).
     */
    setCountry: applyCountry,
    /**
     * Вызываешь этот метод, когда пользователь выбирает язык интерфейса.
     * Например: setLanguage("sk") или setLanguage("en").
     */
    setLanguage: applyLanguage,
  };

  return (
    <LocaleContext.Provider value={value}>
      {children}
    </LocaleContext.Provider>
  );
};

export const useLocale = () => useContext(LocaleContext);
